import { Token } from "@uniswap/sdk-core"
import { FeeAmount, Pool, Position } from "@uniswap/v3-sdk"

import type { TokenStore } from "@/domains/tokens/token-store"

import { toBigInt, toJSBI } from "./jsbi-bigint"
import { getTickAtSqrtRatio } from "./tick-math"

function toSdkToken(token: TokenStore): Token {
  return new Token(token.chainId, token.address.toString(), token.decimals, token.symbol, token.name)
}

function makePool(args: {
  sqrtPriceX96: bigint
  token0: TokenStore
  token1: TokenStore
  fee: number
}): Pool {
  const tick = getTickAtSqrtRatio(args.sqrtPriceX96)
  return new Pool(
    toSdkToken(args.token0),
    toSdkToken(args.token1),
    args.fee as FeeAmount,
    toJSBI(args.sqrtPriceX96),
    toJSBI(0n),
    tick,
  )
}

export function positionFromAmount0(args: {
  sqrtPriceX96: bigint
  tickLower: number
  tickUpper: number
  amount0: bigint
  token0: TokenStore
  token1: TokenStore
  fee: number
}): { liquidity: bigint; amount0: bigint; amount1: bigint } {
  const pool = makePool(args)
  const position = Position.fromAmount0({
    pool,
    tickLower: args.tickLower,
    tickUpper: args.tickUpper,
    amount0: toJSBI(args.amount0),
    useFullPrecision: true,
  })
  return {
    liquidity: toBigInt(position.liquidity),
    amount0: toBigInt(position.mintAmounts.amount0),
    amount1: toBigInt(position.mintAmounts.amount1),
  }
}

export function positionFromAmount1(args: {
  sqrtPriceX96: bigint
  tickLower: number
  tickUpper: number
  amount1: bigint
  token0: TokenStore
  token1: TokenStore
  fee: number
}): { liquidity: bigint; amount0: bigint; amount1: bigint } {
  const pool = makePool(args)
  const position = Position.fromAmount1({
    pool,
    tickLower: args.tickLower,
    tickUpper: args.tickUpper,
    amount1: toJSBI(args.amount1),
  })
  return {
    liquidity: toBigInt(position.liquidity),
    amount0: toBigInt(position.mintAmounts.amount0),
    amount1: toBigInt(position.mintAmounts.amount1),
  }
}

/**
 * Return the amounts of token0 and token1 owed by a position with the given liquidity
 * at the given pool sqrt price. Pure bigint math; no tokens required.
 */
export function getAmountsForLiquidity(args: {
  sqrtPriceX96: bigint
  tickLower: number
  tickUpper: number
  liquidity: bigint
}): { amount0: bigint; amount1: bigint } {
  const stub0 = new Token(1, "0x0000000000000000000000000000000000000001", 18, "A", "A")
  const stub1 = new Token(1, "0x0000000000000000000000000000000000000002", 18, "B", "B")
  const tick = getTickAtSqrtRatio(args.sqrtPriceX96)
  const pool = new Pool(
    stub0,
    stub1,
    FeeAmount.MEDIUM,
    toJSBI(args.sqrtPriceX96),
    toJSBI(0n),
    tick,
  )
  const position = new Position({
    pool,
    tickLower: args.tickLower,
    tickUpper: args.tickUpper,
    liquidity: toJSBI(args.liquidity),
  })
  return {
    amount0: toBigInt(position.amount0.quotient),
    amount1: toBigInt(position.amount1.quotient),
  }
}
